import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import stylesTable from './catalog-item-table.scss?inline';
import stylesTile from './catalog-item-tile.scss?inline';
import { AVAILABLE_COUNT_PROP, CATALOG_VARIANT_TABLE, CATALOG_VARIANT_TILE, EXPANDED_ATTRIBUTE } from '../constants';
import { map } from 'lit/directives/map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import propertyArrayConverter from '@/helpers/propertyArrayConverter.js';
import { isLaptopMaxWidth } from '@/helpers';
import { cache } from 'lit/directives/cache.js';

export default class SotbitCatalogItem extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(stylesTile)}
        `,
        css`
            ${unsafeCSS(stylesTable)}
        `,
    ];

    static properties = {
        hideImg: { type: Boolean, attribute: 'hide-img' },
        isOffers: { type: Boolean, attribute: 'is-offers' },
        isOffer: { type: Boolean, attribute: 'offer' },
        isToggleOffers: { type: Boolean, attribute: 'toggle-offers' },
        offersOpen: { type: Boolean, attribute: 'offers-open', reflect: true },
        variant: { type: String },
        articleProp: { type: String, attribute: 'article-prop' },
        properties: {
            type: Array,
            converter: propertyArrayConverter,
        },
        tableProperties: {
            type: Array,
            converter: propertyArrayConverter,
            attribute: 'table-properties',
        },
    };

    constructor() {
        super();

        this.hideImg = false;
        this.isOffers = false;
        this.isToggleOffers = false;
        this.offersOpen = false;
        this.variant = CATALOG_VARIANT_TILE;
        this.properties = [];
        this.tableProperties = [];
        this.articleProp = '';
        this._itemTileRef = createRef();
        this._itemTilePropertyListRef = createRef();
        this._itemTableRowRef = createRef();
        this._isMouseOver = false;
        this._stickyPinned = false;
        this._stickyHeight = 0;
        this._stickyRaf = null;
        this._scrollPanelEl = null;
        this._adjustItemTilePosition = this._adjustItemTilePosition.bind(this);
        this._cleanUpItemTilePosition = this._cleanUpItemTilePosition.bind(this);
        this._onStickyScroll = this._onStickyScroll.bind(this);
    }

    _handleItemMouseover() {
        this.setAttribute('hovered', '');
    }

    _handleItemMouseout() {
        this.removeAttribute('hovered');
    }

    _handleOpenOffers(e) {
        if (e.target.closest('[slot]') && e.target !== e.currentTarget) {
            return;
        }
        this.offersOpen = !this.offersOpen;
    }

    _handleMouseover() {
        if (!this._isMouseOver) {
            this._isMouseOver = true;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._adjustItemTilePosition();
                this.setAttribute(EXPANDED_ATTRIBUTE, '');
            }
        }
    }

    _handleMouseout(event) {
        if (!this._isMouseOver) {
            return;
        }

        if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
            this._isMouseOver = false;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._cleanUpItemTilePosition();
                this.removeAttribute(EXPANDED_ATTRIBUTE);
            }
        }
    }

    _adjustItemTilePosition() {
        if (!this._itemTileRef?.value || !this._itemTilePropertyListRef?.value) {
            return;
        }

        const itemNode = this._itemTileRef.value;
        const propertyListNode = this._itemTilePropertyListRef.value;

        itemNode?.style?.setProperty('--ci-property-list-height', `${propertyListNode.offsetHeight}px`);
    }

    _cleanUpItemTilePosition() {
        if (!this._itemTileRef?.value) {
            return;
        }

        this._itemTileRef.value.removeAttribute('style');
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('mouseover', this._handleMouseover);
        this.addEventListener('mouseout', this._handleMouseout);
        document.addEventListener('scroll', this._onStickyScroll, { passive: true, capture: true });
        window.addEventListener('resize', this._onStickyScroll, { passive: true });
        this._bindScrollPanelScroll();

        if (isLaptopMaxWidth()) {
            this.variant = CATALOG_VARIANT_TILE;
            this.setAttribute('variant', CATALOG_VARIANT_TILE);
        }
    }

    disconnectedCallback() {
        document.removeEventListener('scroll', this._onStickyScroll, { capture: true });
        window.removeEventListener('resize', this._onStickyScroll);
        this._unbindScrollPanelScroll();
        this._unpinTableRow();
        super.disconnectedCallback();
    }

    updated(changedProperties) {
        if (
            changedProperties.has('offersOpen') ||
            changedProperties.has('isOffers') ||
            changedProperties.has('variant')
        ) {
            this._bindScrollPanelScroll();
            // wait for offers layout after toggle
            requestAnimationFrame(() => this._updateStickyRow());
        }
    }

    _onStickyScroll() {
        if (this._stickyRaf) {
            return;
        }

        this._stickyRaf = requestAnimationFrame(() => {
            this._stickyRaf = null;
            this._updateStickyRow();
        });
    }

    _bindScrollPanelScroll() {
        const panel = this.closest('sotbit-scroll-panel');
        const scrollEl = panel?.shadowRoot?.querySelector('.simplebar-content-wrapper');

        if (!scrollEl || scrollEl === this._scrollPanelEl) {
            return;
        }

        this._unbindScrollPanelScroll();
        this._scrollPanelEl = scrollEl;
        scrollEl.addEventListener('scroll', this._onStickyScroll, { passive: true });
    }

    _unbindScrollPanelScroll() {
        this._scrollPanelEl?.removeEventListener('scroll', this._onStickyScroll);
        this._scrollPanelEl = null;
    }

    _getStickyTop() {
        const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
        const parsed = parseFloat(value);

        return Number.isFinite(parsed) ? parsed : 0;
    }

    _getTableGridInner() {
        return this.closest('.catalog-section-inner');
    }

    _getTableRow() {
        return this._itemTableRowRef?.value || this.renderRoot?.querySelector?.('.catalog-item');
    }

    _updateStickyRow() {
        if (
            this.variant !== CATALOG_VARIANT_TABLE ||
            !this.isOffers ||
            !this.offersOpen ||
            isLaptopMaxWidth()
        ) {
            this._unpinTableRow();
            return;
        }

        const row = this._getTableRow();
        const offers = this.renderRoot?.querySelector?.('.catalog-item-offers');
        const inner = this._getTableGridInner();

        if (!row || !offers || !inner) {
            this._unpinTableRow();
            return;
        }

        if (getComputedStyle(offers).display === 'none') {
            this._unpinTableRow();
            return;
        }

        const stickyTop = this._getStickyTop();
        const rowRect = row.getBoundingClientRect();
        const offersRect = offers.getBoundingClientRect();
        const spacer = this.renderRoot.querySelector('.catalog-item-sticky-spacer');
        // When row is fixed, natural position is the spacer in document flow
        const anchorTop = spacer ? spacer.getBoundingClientRect().top : rowRect.top;
        const rowHeight = (this._stickyPinned ? this._stickyHeight : rowRect.height) || rowRect.height;
        const blockBottom = offersRect.bottom;

        // Still above the stick line — keep in normal flow
        if (anchorTop >= stickyTop + 1) {
            this._unpinTableRow();
            return;
        }

        // Offers block already scrolled away
        if (blockBottom <= stickyTop + 1) {
            this._unpinTableRow();
            return;
        }

        // Slide up with the end of offers (classic sticky end behavior)
        const pinnedTop = Math.min(stickyTop, blockBottom - rowHeight);
        this._pinTableRow(row, inner, pinnedTop, rowHeight);
    }

    _pinTableRow(row, inner, stickyTop, rowHeight) {
        if (!this._stickyPinned) {
            if (rowHeight < 1) {
                return;
            }

            this._stickyPinned = true;
            this._stickyHeight = rowHeight;

            let spacer = this.renderRoot.querySelector('.catalog-item-sticky-spacer');
            if (!spacer) {
                spacer = document.createElement('div');
                spacer.className = 'catalog-item-sticky-spacer';
                row.after(spacer);
            }
            spacer.style.cssText = `grid-column:1/-1;height:${rowHeight}px;pointer-events:none;`;
        }

        const innerRect = inner.getBoundingClientRect();
        const columns = getComputedStyle(inner).gridTemplateColumns;

        row.style.setProperty('position', 'fixed', 'important');
        row.style.setProperty('top', `${stickyTop}px`, 'important');
        row.style.setProperty('left', `${innerRect.left}px`, 'important');
        row.style.setProperty('width', `${innerRect.width}px`, 'important');
        row.style.setProperty('z-index', '20', 'important');
        row.style.setProperty('display', 'grid', 'important');
        row.style.setProperty('grid-template-columns', columns, 'important');
        row.style.setProperty('box-sizing', 'border-box', 'important');
        row.style.setProperty('background', 'var(--white, #fff)', 'important');
        row.style.setProperty('margin', '0', 'important');
        row.classList.add('catalog-item--sticky-active');
    }

    _unpinTableRow() {
        const row = this._getTableRow();
        if (row) {
            row.classList.remove('catalog-item--sticky-active');
            if (this._stickyPinned) {
                row.removeAttribute('style');
            }
        }

        this.renderRoot?.querySelector?.('.catalog-item-sticky-spacer')?.remove();
        this._stickyPinned = false;
        this._stickyHeight = 0;
    }

    templateTable() {
        return html` <slot></slot>
            <div
                class="catalog-item"
                ${ref(this._itemTableRowRef)}
                @mouseover="${this._handleItemMouseover}"
                @mouseout="${this._handleItemMouseout}"
            >
                ${this.isToggleOffers
                    ? html`
                          <sotbit-catalog-section-table-cell
                              left-rounded
                              variant="catalog-item"
                              settings
                              class="column"
                          >
                              <div slot="content">
                                  <div class="offers-toggle">
                                      ${this.isOffers
                                          ? html` <div @click="${this._handleOpenOffers}">
                                                ${this.isOffers
                                                    ? html`<sotbit-icon
                                                          @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                                                          icon="chevron-right"
                                                          size="m"
                                                          background
                                                          variant="secondary"
                                                          pointer
                                                      ></sotbit-icon>`
                                                    : nothing}
                                            </div>`
                                          : ''}
                                  </div>
                              </div>
                          </sotbit-catalog-section-table-cell>
                      `
                    : html`
                          <sotbit-catalog-section-table-cell settings class="column">
                              <div slot="content"></div>
                          </sotbit-catalog-section-table-cell>
                      `}

                <sotbit-catalog-section-table-cell
                    ?left-rounded="${this.isToggleOffers !== true}"
                    ?left-padding-0="${this.isToggleOffers}"
                    right-border
                    variant="catalog-item"
                    product-name
                    class="column column--name"
                >
                    <div slot="content">
                        <div class="info">
                            ${this.hideImg !== true
                                ? html` <div class="img">
                                      <slot name="img"></slot>
                                  </div>`
                                : nothing}
                            <div class="name-wrapper" id="name">
                                <div class="tags">
                                    <slot name="tag"></slot>
                                </div>
                                <div class="name">
                                    <slot name="name"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </sotbit-catalog-section-table-cell>

                ${map(
                    this.tableProperties,
                    (property) => html`
                        <sotbit-catalog-section-table-cell
                            @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                            class="column"
                            variant="catalog-item"
                            part="column-property"
                        >
                            <div slot="content">
                                <slot ?data-article="${property === this.articleProp}" name="${property}"></slot>
                            </div>
                        </sotbit-catalog-section-table-cell>
                    `
                )}

                <sotbit-catalog-section-table-cell
                    left-border
                    right-rounded
                    right-sticky
                    class="column column--counter"
                    variant="catalog-item"
                >
                    <div slot="content" class="counter-wrapper">
                        <div class="actions">
                            <div class="counter">
                                <slot name="counter"></slot>
                            </div>
                            <div class="compare">
                                <slot name="compare"></slot>
                            </div>
                        </div>
                        <slot name="min-price"></slot>
                    </div>
                </sotbit-catalog-section-table-cell>
            </div>
            ${this.isOffers
                ? html`<div class="catalog-item-offers">
                      <div class="outline"></div>
                      <slot name="offers"></slot>
                  </div>`
                : ''}`;
    }

    templateTile() {
        return html` <div class="catalog-item-back">
                <slot></slot>
            </div>
            <div class="catalog-item" ${ref(this._itemTileRef)} part="catalog-item">
                <div class="catalog-item-img">
                    <div class="catalog-item-actions">
                        <slot name="compare"></slot>
                    </div>
                    <slot name="img"></slot>
                    <div class="catalog-item-labels">
                        <slot name="tag"></slot>
                    </div>
                </div>
                <div class="catalog-item-info">
                    <div part="catalog-item-prices" class="catalog-item-prices">
                        <span class="catalog-item-price">
                            <slot name="min-price"></slot>
                        </span>
                        <span class="catalog-item-price catalog-item-price--old">
                            <slot name="min-price-old"></slot>
                        </span>
                    </div>

                    <div class="catalog-item-name" part="catalog-item-name">
                        <slot name="name"></slot>
                    </div>

                    ${map(this.properties, (property) => {
                        if (property !== this.articleProp) {
                            return;
                        }

                        return html`
                            <div class="catalog-item-article" part="catalog-item-article">
                                <slot name="${property}"></slot>
                            </div>
                        `;
                    })}
                    ${map(this.properties, (property) => {
                        if (property !== AVAILABLE_COUNT_PROP) {
                            return;
                        }

                        return html`
                            <div class="catalog-item-available" part="catalog-item-available">
                                <slot name="${property}"></slot>
                            </div>
                        `;
                    })}
                </div>
                <div class="catalog-item-counter">
                    <slot name="counter"></slot>
                </div>
                <div
                    part="catalog-item-property-list"
                    class="catalog-item-property-list"
                    ${ref(this._itemTilePropertyListRef)}
                >
                    <sotbit-scroll-panel
                        class="catalog-item-property-list-scroll"
                        part="catalog-item-property-list-scroll"
                    >
                        <div class="catalog-item-property-list-scroll__content">
                            ${map(this.properties, (property) => {
                                if (property === this.articleProp || property === AVAILABLE_COUNT_PROP) {
                                    return;
                                }

                                return html` <slot name="${property}"></slot> `;
                            })}
                        </div>
                    </sotbit-scroll-panel>
                </div>
            </div>`;
    }

    render() {
        return html`${cache(this.variant === CATALOG_VARIANT_TABLE ? this.templateTable() : this.templateTile())}`;
    }
}
